import {
  type DeadLetterHandler,
  type DeviceEvent,
  type DeviceEventsClient,
  type DeviceEventsReporterOptions,
} from '../types/deviceEvents.type';
import { isRetryableError } from './deviceEventsRetry.util';

type QueuedEvent = {
  attempts: number;
  event: DeviceEvent;
};

const DEFAULT_MAX_ATTEMPTS = 5;
const DEFAULT_FLUSH_DELAY_MS = 1_000;

const defaultOnDeadLetter: DeadLetterHandler = () => {};

export const createDeviceEventsReporter = (
  client: DeviceEventsClient,
  options: DeviceEventsReporterOptions = {},
) => {
  let activeChunk: QueuedEvent[] = [];
  const batchSize = 10;
  let beaconFlushRequested = false;
  let disposed = false;
  const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const maxQueueSize = options.maxQueueSize;
  const flushDelayMs = options.flushDelayMs ?? DEFAULT_FLUSH_DELAY_MS;
  const onDeadLetter = options.onDeadLetter ?? defaultOnDeadLetter;
  const onEmit = options.onEmit;

  let flushTimer: null | number = null;
  let flushing = false;
  let queue: QueuedEvent[] = [];
  let toRequeue: QueuedEvent[] = [];

  const clearFlushTimer = () => {
    if (flushTimer === null) return;
    clearTimeout(flushTimer);
    flushTimer = null;
  };

  const deadLetter = (event: DeviceEvent, error: unknown) => {
    onDeadLetter(event, error);
  };

  const ensureFlushTimer = () => {
    if (flushTimer !== null) return;
    if (disposed) return;
    if (queue.length === 0) return;

    flushTimer = window.setTimeout(() => {
      flushTimer = null;
      void flush();
    }, flushDelayMs);
  };

  const flushBeacon = () => {
    if (disposed) return;

    const events = [...activeChunk, ...queue, ...toRequeue];

    beaconFlushRequested = flushing;
    activeChunk = [];
    queue = [];
    toRequeue = [];

    for (const item of events) client.emitBeacon(item.event);

    clearFlushTimer();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') flushBeacon();
  };

  const onPageHide = () => {
    flushBeacon();
  };

  window.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);

  const dispose = () => {
    if (disposed) return;

    disposed = true;
    beaconFlushRequested = true;
    activeChunk = [];
    queue = [];
    toRequeue = [];

    window.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
    clearFlushTimer();
  };

  const flush = async () => {
    if (disposed) return;

    if (flushing) {
      ensureFlushTimer();
      return;
    }

    if (queue.length === 0) return;

    flushing = true;
    beaconFlushRequested = false;
    clearFlushTimer();

    const chunk = queue.splice(0, batchSize);
    activeChunk = [...chunk];
    toRequeue = [];
    let shouldFlushImmediately = false;

    try {
      for (const item of chunk) {
        if (beaconFlushRequested || disposed) break;

        try {
          // Events must emit sequentially so retry/requeue state stays deterministic.
          // eslint-disable-next-line no-await-in-loop
          await client.emit(item.event);
          onEmit?.(item.event);
        } catch (error) {
          if (beaconFlushRequested || disposed) break;

          if (!isRetryableError(error) || item.attempts + 1 >= maxAttempts) {
            deadLetter(item.event, error);
            continue;
          }

          toRequeue.push({
            attempts: item.attempts + 1,
            event: item.event,
          });
        } finally {
          activeChunk = activeChunk.filter((active) => active !== item);
        }
      }

      shouldFlushImmediately = queue.length >= batchSize;

      if (!beaconFlushRequested && !disposed && toRequeue.length > 0) {
        queue.push(...toRequeue);
        toRequeue = [];
      }
    } finally {
      activeChunk = [];
      flushing = false;

      if (disposed) clearFlushTimer();
      else if (queue.length === 0) clearFlushTimer();
      else if (shouldFlushImmediately) void flush();
      else ensureFlushTimer();
    }
  };

  const track = (event: DeviceEvent) => {
    if (disposed) return;

    if (maxQueueSize !== undefined && queue.length >= maxQueueSize) {
      const dropped = queue.shift()!;
      deadLetter(dropped.event, new Error('[device-events] queue full — oldest event dropped'));
    }

    queue.push({ attempts: 0, event });

    if (queue.length >= batchSize) {
      void flush();
      return;
    }

    ensureFlushTimer();
  };

  return {
    dispose,
    flush,
    flushBeacon,
    track,
  };
};
